import { useCallback, useEffect, useState } from "react"
import { OfflineIpqcBundle } from "@/ipqc/lib/offline-store"
import useStore from "@/store"
import {
  ensureOfflineBundleAvailable,
  getIpqcOfflineBundleErrorMessage,
} from "@/services/ipqc"
import { useInternetStatus } from "@/hooks/use-internet-status"

interface UseEnsureIpqcOfflineBundleOptions {
  enabled?: boolean
}

interface UseEnsureIpqcOfflineBundleResult {
  bundle: OfflineIpqcBundle | null
  isLoading: boolean
  error: string | null
  ensureBundle: () => Promise<OfflineIpqcBundle | null>
}

function hasHandoverEmployees(bundle: OfflineIpqcBundle | null): boolean {
  return !!bundle && Array.isArray(bundle.employees)
}

export function useEnsureIpqcOfflineBundle(
  options: UseEnsureIpqcOfflineBundleOptions = {}
): UseEnsureIpqcOfflineBundleResult {
  const { enabled = true } = options
  const bundle = useStore((state) => state.ipqc.offline.bundle)
  const { isOnline } = useInternetStatus()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ensureBundle = useCallback(async () => {
    if (!enabled) {
      return bundle
    }

    if (hasHandoverEmployees(bundle)) {
      setError(null)
      return bundle
    }

    setIsLoading(true)

    try {
      const resolvedBundle = await ensureOfflineBundleAvailable({ isOnline })
      setError(null)
      return resolvedBundle
    } catch (err) {
      setError(getIpqcOfflineBundleErrorMessage(err))
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [bundle, enabled, isOnline])

  useEffect(() => {
    if (!enabled || hasHandoverEmployees(bundle)) {
      return
    }

    void ensureBundle().catch(() => {})
  }, [bundle, enabled, ensureBundle])

  useEffect(() => {
    if (hasHandoverEmployees(bundle)) {
      setError(null)
    }
  }, [bundle])

  return {
    bundle,
    isLoading,
    error,
    ensureBundle,
  }
}
